import { useState, useRef, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot, LeadError } from '../useLeadSubmit';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const DEFAULT_FIELDS = [
    { name: 'nombre', label: 'Nombre completo', type: 'text', required: true },
    { name: 'email', label: 'Correo electronico', type: 'email', required: true },
    { name: 'telefono', label: 'Telefono', type: 'tel', required: false },
    { name: 'mensaje', label: 'Mensaje', type: 'textarea', required: false },
];

const SIMPLE_FIELDS = [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true },
    { name: 'email', label: 'Correo electronico', type: 'email', required: true },
    { name: 'mensaje', label: 'Mensaje', type: 'textarea', required: true },
];

const COMPLETE_FIELDS = [
    { name: 'nombre', label: 'Nombre completo', type: 'text', required: true },
    { name: 'email', label: 'Correo electronico', type: 'email', required: true },
    { name: 'telefono', label: 'Telefono', type: 'tel', required: true },
    { name: 'carrera', label: 'Carrera de interes', type: 'select', required: true },
    { name: 'mensaje', label: 'Mensaje', type: 'textarea', required: false },
];

export default function FormSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, careers = [], currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const accent = config.accent_color || primaryColor;

    const careerField = { name: 'carrera', label: 'Carrera de interes', type: 'select', required: false };
    const baseFields = content.fields?.length > 0
        ? content.fields
        : variant === 'complete' ? COMPLETE_FIELDS
        : variant === 'simple' ? SIMPLE_FIELDS
        : DEFAULT_FIELDS;

    // Insert career field if enabled and not already present
    const fields = (config.show_career_field && !baseFields.some(f => f.name === 'carrera'))
        ? [...baseFields.filter(f => f.type !== 'textarea'), careerField, ...baseFields.filter(f => f.type === 'textarea')]
        : baseFields;

    const [formData, setFormData] = useState({});
    const leadSubmit = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'form',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: content.title || 'Formulario',
    });
    const submitted = leadSubmit.submitted;
    const submitting = leadSubmit.submitting;

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        leadSubmit.submit(formData);
    };

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    const renderField = (field, i) => {
        const inputStyle = s.dark
            ? { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }
            : { backgroundColor: '#ffffff', borderColor: '#d1d5db', color: s.text };
        const baseClass = `w-full border rounded-lg px-4 py-3 text-sm focus:ring-2 focus:outline-none transition-all ${
            s.dark ? 'placeholder-white/50' : 'placeholder-gray-400'
        }`;

        if (field.type === 'textarea') {
            return (
                <textarea
                    key={field.name}
                    placeholder={field.label}
                    required={field.required}
                    rows={3}
                    value={formData[field.name] || ''}
                    onChange={e => handleChange(field.name, e.target.value)}
                    className={`${baseClass} ${variant === 'inline' ? '' : 'col-span-full'}`}
                    style={{ '--tw-ring-color': accent, ...inputStyle }}
                />
            );
        }
        if (field.type === 'select') {
            return (
                <select
                    key={field.name}
                    required={field.required}
                    value={formData[field.name] || ''}
                    onChange={e => handleChange(field.name, e.target.value)}
                    className={baseClass}
                    style={{ '--tw-ring-color': accent, ...inputStyle }}
                >
                    <option value="">{field.label}</option>
                    {(careers || []).map((c, ci) => (
                        <option key={ci} value={c.name || c.title || c}>{c.name || c.title || c}</option>
                    ))}
                    {(!careers || careers.length === 0) && (
                        <>
                            <option value="carrera-1">Carrera 1</option>
                            <option value="carrera-2">Carrera 2</option>
                            <option value="carrera-3">Carrera 3</option>
                        </>
                    )}
                </select>
            );
        }
        return (
            <input
                key={field.name}
                type={field.type || 'text'}
                placeholder={field.label}
                required={field.required}
                value={formData[field.name] || ''}
                onChange={e => handleChange(field.name, e.target.value)}
                className={baseClass}
                style={{ '--tw-ring-color': accent, ...inputStyle }}
            />
        );
    };

    if (submitted) {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="text-center py-16">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: hexToRgba(accent, 0.13) }}>
                            <svg className="w-8 h-8" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold mb-2" style={{ color: s.text }}>{content.success_title || 'Mensaje enviado'}</h3>
                        <p className="text-sm" style={{ color: s.textSub }}>
                            {content.success_message || 'Gracias por contactarnos'}
                        </p>
                        <button
                            onClick={() => { leadSubmit.reset(); setFormData({}); }}
                            className="mt-6 text-sm font-medium hover:underline"
                            style={{ color: accent }}
                        >
                            {content.reset_text || 'Enviar otro mensaje'}
                        </button>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Inline variant
    if (variant === 'inline') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-4xl mx-auto relative">
                        <Honeypot onFill={leadSubmit.setHoneypot} />
                        {fields.filter(f => f.type !== 'textarea').map(renderField)}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-8 py-3 rounded-lg text-white font-semibold text-sm whitespace-nowrap transition-all hover:opacity-90 disabled:opacity-60 shrink-0"
                            style={{ backgroundColor: accent }}
                        >
                            {submitting ? (content.submitting_text || 'Enviando...') : (content.submit_text || content.cta_text || 'Enviar')}
                        </button>
                    </form>
                    <LeadError error={leadSubmit.error} />
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Multi-step variant
    if (variant === 'multi-step') {
        return (
            <MultiStepForm
                fields={fields}
                content={content}
                config={config}
                formData={formData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                renderField={renderField}
                leadSubmit={leadSubmit}
                submitting={submitting}
                accent={accent}
                s={s}
                sectionCls={sectionCls}
                anim={anim}
                sectionRef={ref}
            />
        );
    }

    // Simple & Complete variants
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="max-w-2xl mx-auto">
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
                        <Honeypot onFill={leadSubmit.setHoneypot} />
                        {fields.map(renderField)}
                        <div className="col-span-full">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full px-8 py-3.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-60"
                                style={{ backgroundColor: accent }}
                            >
                                {submitting ? (content.submitting_text || 'Enviando...') : (content.submit_text || content.cta_text || 'Enviar mensaje')}
                            </button>
                        </div>
                    </form>
                    <LeadError error={leadSubmit.error} />
                    {content.disclaimer && (
                        <p className="text-xs mt-4 text-center" style={{ color: s.textSub }}>{content.disclaimer}</p>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/*  Multi-step form sub-component                                      */
/* ------------------------------------------------------------------ */
function MultiStepForm({
    fields, content, config, formData, handleChange, handleSubmit,
    renderField, leadSubmit, submitting, accent, s, sectionCls, anim, sectionRef,
}) {
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
    const [stepErrors, setStepErrors] = useState({});
    const containerRef = useRef(null);

    // Build steps: use content.steps if provided, otherwise auto-split fields into groups of 3
    const steps = (() => {
        if (content.steps && content.steps.length > 0) {
            // Map step definitions to actual field objects
            return content.steps.map((step, idx) => {
                const stepFields = step.fields
                    ? step.fields.map(fname => fields.find(f => f.name === fname)).filter(Boolean)
                    : [];
                return { title: step.title || `Paso ${idx + 1}`, fields: stepFields };
            });
        }
        // Auto-split into groups of 3
        const groupSize = 3;
        const result = [];
        for (let i = 0; i < fields.length; i += groupSize) {
            result.push({
                title: `Paso ${result.length + 1}`,
                fields: fields.slice(i, i + groupSize),
            });
        }
        return result;
    })();

    const totalSteps = steps.length;
    const isLastStep = currentStep === totalSteps - 1;

    // Validate the current step's required fields
    const validateStep = () => {
        const currentFields = steps[currentStep]?.fields || [];
        const errors = {};
        let valid = true;
        for (const field of currentFields) {
            if (field.required && !formData[field.name]?.toString().trim()) {
                errors[field.name] = true;
                valid = false;
            }
            // Basic email validation
            if (field.type === 'email' && formData[field.name]) {
                const emailVal = formData[field.name].trim();
                if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
                    errors[field.name] = true;
                    valid = false;
                }
            }
        }
        setStepErrors(errors);
        return valid;
    };

    const goNext = () => {
        if (!validateStep()) return;
        setDirection(1);
        setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
        setStepErrors({});
    };

    const goBack = () => {
        setDirection(-1);
        setCurrentStep(prev => Math.max(prev - 1, 0));
        setStepErrors({});
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validateStep()) return;
        handleSubmit(e);
    };

    // Render field with optional error highlight
    const renderStepField = (field, i) => {
        const rendered = renderField(field, i);
        if (!stepErrors[field.name]) return rendered;
        // Wrap with error border indicator
        return (
            <div key={`err-${field.name}`} className="relative">
                <div className="ring-2 ring-red-400 rounded-lg">
                    {renderField(field, i)}
                </div>
                <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
            </div>
        );
    };

    // CSS transition style
    const slideStyle = {
        transform: `translateX(0)`,
        transition: 'transform 0.35s ease, opacity 0.35s ease',
        opacity: 1,
    };

    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={sectionRef} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="max-w-2xl mx-auto">
                    {/* Step indicator */}
                    <div className="flex items-center justify-center mb-8">
                        {steps.map((step, idx) => (
                            <div key={idx} className="flex items-center">
                                {/* Step circle */}
                                <div className="flex flex-col items-center">
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 border-2"
                                        style={{
                                            backgroundColor: idx <= currentStep ? accent : 'transparent',
                                            borderColor: idx <= currentStep ? accent : (s.dark ? 'rgba(255,255,255,0.3)' : '#d1d5db'),
                                            color: idx <= currentStep ? '#ffffff' : (s.dark ? 'rgba(255,255,255,0.5)' : '#9ca3af'),
                                        }}
                                    >
                                        {idx < currentStep ? (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : (
                                            idx + 1
                                        )}
                                    </div>
                                    <span
                                        className="text-xs mt-1.5 whitespace-nowrap font-medium hidden sm:block"
                                        style={{ color: idx <= currentStep ? s.text : s.textSub }}
                                    >
                                        {step.title}
                                    </span>
                                </div>
                                {/* Connector line */}
                                {idx < totalSteps - 1 && (
                                    <div
                                        className="w-10 sm:w-16 h-0.5 mx-1 sm:mx-2 mb-5 sm:mb-4 transition-all duration-300"
                                        style={{
                                            backgroundColor: idx < currentStep ? accent : (s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb'),
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Step title (mobile) */}
                    <p className="text-center text-sm font-medium mb-4 sm:hidden" style={{ color: s.text }}>
                        {steps[currentStep]?.title}
                    </p>

                    {/* Form with animated steps */}
                    <form onSubmit={onSubmit} className="relative overflow-hidden">
                        <Honeypot onFill={leadSubmit.setHoneypot} />
                        <div className="relative" ref={containerRef}>
                            {steps.map((step, idx) => (
                                <div
                                    key={idx}
                                    className="transition-all duration-350 ease-in-out"
                                    style={{
                                        display: idx === currentStep ? 'block' : 'none',
                                        animation: idx === currentStep
                                            ? (direction > 0 ? 'msSlideInRight 0.35s ease forwards' : 'msSlideInLeft 0.35s ease forwards')
                                            : 'none',
                                    }}
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {step.fields.map((field, fi) => renderStepField(field, fi))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Navigation buttons */}
                        <div className="flex items-center justify-between mt-6 gap-3">
                            {currentStep > 0 ? (
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="px-6 py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-80 border-2"
                                    style={{
                                        borderColor: accent,
                                        color: accent,
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    Anterior
                                </button>
                            ) : (
                                <div />
                            )}

                            {isLastStep ? (
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-8 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-60"
                                    style={{ backgroundColor: accent }}
                                >
                                    {submitting ? (content.submitting_text || 'Enviando...') : (content.submit_text || content.cta_text || 'Enviar mensaje')}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={goNext}
                                    className="px-8 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg"
                                    style={{ backgroundColor: accent }}
                                >
                                    Siguiente
                                </button>
                            )}
                        </div>
                    </form>

                    <LeadError error={leadSubmit.error} />
                    {content.disclaimer && (
                        <p className="text-xs mt-4 text-center" style={{ color: s.textSub }}>{content.disclaimer}</p>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>

            {/* Keyframe animations for step transitions */}
            <style>{`
                @keyframes msSlideInRight {
                    from { transform: translateX(60px); opacity: 0; }
                    to   { transform: translateX(0); opacity: 1; }
                }
                @keyframes msSlideInLeft {
                    from { transform: translateX(-60px); opacity: 0; }
                    to   { transform: translateX(0); opacity: 1; }
                }
            `}</style>
        </section>
    );
}
